import { apiService } from './apiService';

export interface SentimentoEntry {
  id: string;
  user_id: string;
  emotion: string;
  category?: string;
  note_id?: string;
  entry_date: string;
  created_at: string;
  updated_at: string;
  note?: {
    id: string;
    content: string;
  };
}

export interface CreateSentimentoEntryData {
  emotion: string;
  category?: string;
  noteContent?: string;
  entry_date?: string;
}

class SentimentoService {
  /**
   * Create a new sentimento entry via API (bypasses RLS timing issues)
   */
  async createEntry(data: CreateSentimentoEntryData): Promise<SentimentoEntry | null> {
    // PRD §5.4 — `mood_entry_started`. The service is the canonical site
    // because every UI surface that creates a sentimento entry routes
    // through here (sentiment selector, AI prompt, deep link, etc).
    import('@/utils/clarityEvents')
      .then(({ logMoodEntryStarted }) => logMoodEntryStarted())
      .catch(() => {});

    try {
      console.log('📝 Creating sentimento entry via API:', data.emotion);

      const response = await apiService.post<{
        success: boolean;
        entry?: SentimentoEntry;
        error?: string;
      }>('/api/sentimentos', {
        emotion: data.emotion,
        category: data.category,
        noteContent: data.noteContent,
        entry_date: data.entry_date
      });

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to create sentimento entry');
      }

      console.log('✅ Sentimento entry created via API:', response.data.entry?.id);

      // RJ-3b — invalidate today's recovery plan so the Missões row for
      // `registrar_diario_sentimentos` flips to completed on next visit
      // without waiting for a tab focus. Dynamic import avoids a service-
      // graph cycle (recoveryPlanService transitively imports apiService).
      import('./recoveryPlanService')
        .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
        .catch(() => {});

      // PRD §4 — `mood_first_logged` macro event. Idempotent across sessions
      // via AsyncStorage flag inside the helper. mood_value omitted because
      // `data.emotion` is a string label, not the 1–5 scale; the dictionary
      // key expects a number, so we leave it undefined here.
      import('@/utils/clarityEvents')
        .then(({ logMoodFirstLogged }) => logMoodFirstLogged({}))
        .catch(() => {});

      // PRD §5.4 — `mood_entry_submitted` micro event. Pairs with
      // `mood_entry_started` above to give the dashboard both intent and
      // outcome volume from the same canonical site. `has_note` flips
      // when the user attached a note alongside the emotion; mood_value
      // is omitted because `data.emotion` is a string label not a 1–5
      // scale.
      import('@/utils/clarityEvents')
        .then(({ logMoodEntrySubmitted }) =>
          logMoodEntrySubmitted({ has_note: !!data.noteContent })
        )
        .catch(() => {});

      return response.data.entry || null;

    } catch (error) {
      console.error('Error in createEntry:', error);
      return null;
    }
  }

  /**
   * Get user's sentimento entries via API (bypasses RLS timing issues)
   * Note: userId parameter is kept for backwards compatibility but not used (API uses JWT)
   */
  async getUserEntries(_userId: string, limit?: number): Promise<SentimentoEntry[]> {
    try {
      console.log('📋 Loading sentimento entries via API...');

      let url = '/api/sentimentos';
      if (limit) {
        url += `?limit=${limit}`;
      }

      const response = await apiService.get<{
        success: boolean;
        entries?: SentimentoEntry[];
        error?: string;
      }>(url);

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to fetch entries');
      }

      console.log('✅ Sentimento entries loaded via API:', response.data.entries?.length || 0);
      return response.data.entries || [];

    } catch (error) {
      console.error('Error in getUserEntries:', error);
      return [];
    }
  }

  /**
   * Get sentimento entries by date via API (bypasses RLS timing issues)
   * Note: userId parameter is kept for backwards compatibility but not used (API uses JWT)
   */
  async getEntriesByDate(_userId: string, date: string): Promise<SentimentoEntry[]> {
    try {
      console.log('📋 Loading sentimento entries by date via API:', date);

      const url = `/api/sentimentos?date=${encodeURIComponent(date)}`;

      const response = await apiService.get<{
        success: boolean;
        entries?: SentimentoEntry[];
        error?: string;
      }>(url);

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to fetch entries by date');
      }

      console.log('✅ Sentimento entries loaded via API:', response.data.entries?.length || 0);
      return response.data.entries || [];

    } catch (error) {
      console.error('Error in getEntriesByDate:', error);
      return [];
    }
  }

  /**
   * Get sentimento entries by emotion via API (bypasses RLS timing issues)
   * Note: userId parameter is kept for backwards compatibility but not used (API uses JWT)
   */
  async getEntriesByEmotion(_userId: string, emotion: string): Promise<SentimentoEntry[]> {
    try {
      console.log('📋 Loading sentimento entries by emotion via API:', emotion);

      const url = `/api/sentimentos?emotion=${encodeURIComponent(emotion)}`;

      const response = await apiService.get<{
        success: boolean;
        entries?: SentimentoEntry[];
        error?: string;
      }>(url);

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to fetch entries by emotion');
      }

      console.log('✅ Sentimento entries loaded via API:', response.data.entries?.length || 0);
      return response.data.entries || [];

    } catch (error) {
      console.error('Error in getEntriesByEmotion:', error);
      return [];
    }
  }

  /**
   * Update a sentimento entry via API (bypasses RLS timing issues)
   */
  async updateEntry(entryId: string, updates: Partial<CreateSentimentoEntryData>): Promise<SentimentoEntry | null> {
    try {
      console.log('📝 Updating sentimento entry via API:', entryId);

      const response = await apiService.put<{
        success: boolean;
        entry?: SentimentoEntry;
        error?: string;
      }>(`/api/sentimentos/${entryId}`, {
        emotion: updates.emotion,
        category: updates.category,
        noteContent: updates.noteContent
      });

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to update sentimento entry');
      }

      console.log('✅ Sentimento entry updated via API:', response.data.entry?.id);
      return response.data.entry || null;

    } catch (error) {
      console.error('Error in updateEntry:', error);
      return null;
    }
  }

  /**
   * Delete a sentimento entry via API (bypasses RLS timing issues)
   */
  async deleteEntry(entryId: string): Promise<boolean> {
    try {
      console.log('🗑️ Deleting sentimento entry via API:', entryId);

      const response = await apiService.delete<{
        success: boolean;
        error?: string;
      }>(`/api/sentimentos/${entryId}`);

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to delete sentimento entry');
      }

      console.log('✅ Sentimento entry deleted via API');
      return true;

    } catch (error) {
      console.error('Error in deleteEntry:', error);
      return false;
    }
  }
}

export const sentimentoService = new SentimentoService();